import { useState, useCallback, useMemo, useEffect } from 'react';
import { DEFAULT_PAGE_SIZE, type PaginationParams } from '@/types/pagination';

interface UsePaginationOptions {
  defaultPageSize?: number;
  persistInUrl?: boolean;
}

export function usePagination(options: UsePaginationOptions = {}) {
  const { defaultPageSize = DEFAULT_PAGE_SIZE, persistInUrl = true } = options;
  
  // Get initial values from URL or defaults
  const getInitialPage = () => {
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('page')) || 1;
    }
    return 1;
  };
  
  const getInitialPageSize = () => {
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('pageSize')) || defaultPageSize;
    }
    return defaultPageSize;
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [pageSize, setCurrentPageSize] = useState(getInitialPageSize);
  
  // Update URL when page or pageSize changes
  useEffect(() => {
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(currentPage));
      params.set('pageSize', String(pageSize));
      
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [currentPage, pageSize, persistInUrl]);
  
  const setPage = useCallback((page: number | ((prev: number) => number)) => {
    const newPage = typeof page === 'function' ? page(currentPage) : page;
    setCurrentPage(newPage);
  }, [currentPage]);
  
  const setPageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);
  
  const goToFirstPage = useCallback(() => setPage(1), [setPage]);
  const goToLastPage = useCallback((totalPages: number) => setPage(totalPages), [setPage]);
  const goToNextPage = useCallback(() => setPage(p => p + 1), [setPage]);
  const goToPreviousPage = useCallback(() => setPage(p => Math.max(1, p - 1)), [setPage]);
  
  const resetPagination = useCallback(() => {
    setPage(1);
  }, [setPage]);
  
  const paginationParams: PaginationParams = useMemo(() => ({
    page: currentPage,
    pageSize,
  }), [currentPage, pageSize]);
  
  return {
    currentPage,
    pageSize,
    paginationParams,
    setPage,
    setPageSize,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
  };
}
